import React, { useCallback, useEffect, useRef, useState } from "react";
import ResumePreviewATS from "./ResumePreviewATS";
import ResumePreviewProfessional from "./ResumePreviewProfessional";
import ResumePreviewModern from "./ResumePreviewModern";
import PlaceholderFillPanel from "./PlaceholderFillPanel";
import { downloadResumePDF } from "./ResumePDF";
import { Download, Check, Building2, Briefcase, Zap, FileText, Lock } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { detectPlaceholders, setAtPath } from "../../lib/placeholders";
import UpgradeModal from "../UpgradeModal";

const GUIDANCE = [
  {
    id: "ats",
    label: "ATS Safe",
    Icon: Building2,
    type: "Large corporation, bank or government",
    examples: "RBC · Amazon · Federal Govt · Scotiabank",
    recommendation: "ATS Safe template",
    iconBg: "bg-[#F3F4F6]",
    iconColor: "text-[#111]",
    selectedBg: "bg-[#111]",
    selectedIconBg: "bg-white/10",
  },
  {
    id: "professional",
    label: "Professional",
    Icon: Briefcase,
    type: "Consulting, finance or professional firm",
    examples: "McKinsey · KPMG · Deloitte · TD Bank",
    recommendation: "Professional template",
    iconBg: "bg-[#EFF6FF]",
    iconColor: "text-[#1D4ED8]",
    selectedBg: "bg-[#1D4ED8]",
    selectedIconBg: "bg-white/10",
  },
  {
    id: "modern",
    label: "Modern",
    Icon: Zap,
    type: "Tech company, startup or creative agency",
    examples: "Shopify · Google · Figma · Notion",
    recommendation: "Modern template",
    iconBg: "bg-[#F0F9FF]",
    iconColor: "text-[#0EA5E9]",
    selectedBg: "bg-[#0EA5E9]",
    selectedIconBg: "bg-white/10",
  },
];

const PREVIEW_COMPONENTS = {
  ats:          ResumePreviewATS,
  professional: ResumePreviewProfessional,
  modern:       ResumePreviewModern,
};

export default function ResumeTemplatePicker({
  data,
  candidateName,
  initialTemplate = null,
  onTemplateChange,
  editable = false,
  analysisId = null,
  savedResumeId = null,
}) {
  const { user } = useAuth();
  const isFree = user?.plan !== "premium";
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const [selected, setSelected]     = useState(initialTemplate);
  const [editData, setEditData]     = useState(() => data ? JSON.parse(JSON.stringify(data)) : null);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [downloading, setDownloading]         = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const saveTimer = useRef(null);

  // Reset editData when data prop changes (new analysis loaded)
  useEffect(() => {
    if (data) setEditData(JSON.parse(JSON.stringify(data)));
  }, [data]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const scheduleSave = useCallback((updated) => {
    setSaveStatus("unsaved");
    clearTimeout(saveTimer.current);
    if (!analysisId && !savedResumeId) return;
    saveTimer.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        const calls = [];
        if (analysisId)    calls.push(api.patch(`/resume/analyses/${analysisId}/structured`,     { resume_structured: updated }));
        if (savedResumeId) calls.push(api.patch(`/resume/saved/${savedResumeId}/structured`,     { resume_structured: updated }));
        await Promise.all(calls);
        setSaveStatus("saved");
      } catch {
        setSaveStatus("error");
      }
    }, 800);
  }, [analysisId, savedResumeId]);

  const handleFieldChange = (path, value) => {
    setEditData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      setAtPath(next, path, value);
      scheduleSave(next);
      return next;
    });
  };

  const activeTemplate   = selected || "ats";
  const PreviewComponent = PREVIEW_COMPONENTS[activeTemplate];

  const remaining = editData ? detectPlaceholders(editData).length : 0;
  const canDownload = editable ? remaining === 0 : true;
  const templateLocked = isFree && activeTemplate !== "ats";

  const handleSelect = (id) => {
    setSelected(id);
    onTemplateChange?.(id);
  };

  const doDownloadPDF = async () => {
    setDownloading(true);
    try { await downloadResumePDF(editData, activeTemplate, candidateName); }
    catch (e) { console.error("PDF generation failed:", e); }
    finally { setDownloading(false); }
  };

  const doDownloadDocx = async () => {
    setDownloadingDocx(true);
    try {
      const res = await api.post("/resume/export/docx",
        { resume_structured: editData, name: candidateName || "Resume", template: activeTemplate },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement("a");
      a.href     = url;
      a.download = `${(candidateName || "resume").replace(/\s+/g, "_")}_resume.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("DOCX generation failed:", e);
    } finally {
      setDownloadingDocx(false);
    }
  };

  return (
    <div className="space-y-5">

      {/* ── Guidance panel ──────────────────────────────────────────── */}
      <div className="border border-[#0025D8]/25 bg-gradient-to-br from-[#EFF6FF] to-[#F8FAFF] overflow-hidden">
        <div className="bg-[#0025D8] px-5 py-3 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-white/60" />
          <p className="text-xs font-bold text-white tracking-wide">
            Which type of company are you applying to?
          </p>
        </div>
        <div className="grid grid-cols-3 gap-0 divide-x divide-[#0025D8]/10 p-0">
          {GUIDANCE.map((g) => {
            const isSelected = selected === g.id;
            const cardLocked = isFree && g.id !== "ats";
            return (
              <button
                key={g.id}
                onClick={() => handleSelect(g.id)}
                className={`group relative text-left p-5 transition-all duration-200 focus:outline-none ${
                  isSelected ? `${g.selectedBg} text-white` : "bg-white hover:bg-[#F0F7FF]"
                }`}
              >
                {isSelected && !cardLocked && (
                  <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                {cardLocked && (
                  <div className="absolute top-3 right-3 flex items-center gap-1 px-1.5 py-0.5 bg-[#7C3AED]/20 border border-[#7C3AED]/30 rounded-sm">
                    <Lock className="w-2.5 h-2.5 text-[#7C3AED]" />
                    <span className="text-[9px] font-mono font-bold text-[#7C3AED]">PRO</span>
                  </div>
                )}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors ${isSelected ? g.selectedIconBg : g.iconBg}`}>
                  <g.Icon className={`w-5 h-5 ${isSelected ? "text-white" : g.iconColor}`} />
                </div>
                <p className={`font-semibold text-sm leading-snug mb-1.5 ${isSelected ? "text-white" : "text-[#111]"}`}>{g.type}</p>
                <p className={`text-[10px] leading-relaxed mb-3 ${isSelected ? "text-white/70" : "text-muted-foreground"}`}>{g.examples}</p>
                <div className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm transition-colors ${
                  isSelected ? "bg-white/20 text-white" : "bg-[#0025D8]/8 text-[#0025D8]"
                }`}>
                  → {g.recommendation}
                </div>
              </button>
            );
          })}
        </div>
        {!selected && (
          <div className="px-5 py-2 bg-[#0025D8]/5 border-t border-[#0025D8]/10">
            <p className="text-[10px] text-[#0025D8]/70 font-mono">Click your answer above — we'll select the right template automatically</p>
          </div>
        )}
      </div>

      {/* ── Editor + Preview ─────────────────────────────────────────── */}
      {editable ? (
        /* Side-by-side: panel left, preview right */
        <div className="grid grid-cols-5 gap-4 min-h-[600px]">

          {/* Left: Fill panel */}
          <div className="col-span-2 border border-border overflow-y-auto max-h-[700px] bg-white">
            <PlaceholderFillPanel data={editData} onFieldChange={handleFieldChange} />
          </div>

          {/* Right: Live preview */}
          <div className="col-span-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-[0.2em]">
                  Live preview — {GUIDANCE.find(g => g.id === activeTemplate)?.label}
                </p>
                {analysisId && (
                  <span className={`font-mono text-[9px] transition-colors ${
                    saveStatus === "saved"  ? "text-[#10B981]" :
                    saveStatus === "saving" ? "text-[#0025D8]" :
                    saveStatus === "error"  ? "text-[#E11D48]" :
                                              "text-[#F59E0B]"
                  }`}>
                    {saveStatus === "saved" ? "✓ Saved" : saveStatus === "saving" ? "Saving…" : saveStatus === "error" ? "Save failed" : "Unsaved"}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={templateLocked ? () => setUpgradeOpen(true) : doDownloadPDF}
                  disabled={!templateLocked && (downloading || !editData || !canDownload)}
                  title={templateLocked ? "Premium template — upgrade to download" : !canDownload ? `Fill ${remaining} placeholder${remaining !== 1 ? "s" : ""} to enable download` : "Download as PDF"}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono font-bold transition-colors ${
                    templateLocked
                      ? "bg-[#7C3AED]/20 text-[#7C3AED] border border-[#7C3AED]/30 cursor-pointer hover:bg-[#7C3AED]/30"
                      : canDownload
                        ? "bg-[#0025D8] hover:bg-[#001CB3] text-white"
                        : "bg-[#0025D8]/30 text-white/60 cursor-not-allowed"
                  }`}
                >
                  {templateLocked ? <Lock className="w-3.5 h-3.5" /> : canDownload ? <Download className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  {templateLocked ? "Premium" : downloading ? "Generating…" : "Download PDF"}
                </button>
                <button
                  onClick={templateLocked ? () => setUpgradeOpen(true) : doDownloadDocx}
                  disabled={!templateLocked && (downloadingDocx || !editData || !canDownload)}
                  title={templateLocked ? "Premium template — upgrade to download" : !canDownload ? `Fill ${remaining} placeholder${remaining !== 1 ? "s" : ""} to enable download` : "Download as DOCX"}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono font-bold transition-colors border ${
                    templateLocked
                      ? "border-[#7C3AED]/30 text-[#7C3AED]/70 cursor-pointer hover:bg-[#7C3AED]/5"
                      : canDownload
                        ? "border-[#0025D8] text-[#0025D8] hover:bg-[#0025D8]/5"
                        : "border-[#0025D8]/30 text-[#0025D8]/40 cursor-not-allowed"
                  }`}
                >
                  {templateLocked ? <Lock className="w-3.5 h-3.5" /> : canDownload ? <FileText className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  {templateLocked ? "Premium" : downloadingDocx ? "Generating…" : "Download DOCX"}
                </button>
              </div>
            </div>
            <div className="border border-border overflow-auto max-h-[660px] bg-gray-50">
              <div className="transform scale-[0.85] origin-top-left" style={{ width: "117.6%" }}>
                {editData
                  ? <PreviewComponent data={editData} />
                  : <div className="p-8 text-center text-muted-foreground text-sm">No resume data to preview</div>}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Read-only: full-width preview with download buttons */
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-[0.2em]">
              Live preview — {GUIDANCE.find(g => g.id === activeTemplate)?.label}
            </p>
            <div className="flex gap-2">
              <button
                onClick={templateLocked ? () => setUpgradeOpen(true) : doDownloadPDF}
                disabled={!templateLocked && (downloading || !editData)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono font-bold transition-colors ${
                  templateLocked
                    ? "bg-[#7C3AED]/20 text-[#7C3AED] border border-[#7C3AED]/30 cursor-pointer hover:bg-[#7C3AED]/30"
                    : "bg-[#0025D8] hover:bg-[#001CB3] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                }`}
              >
                {templateLocked ? <Lock className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}
                {templateLocked ? "Premium" : downloading ? "Generating…" : "Download PDF"}
              </button>
              <button
                onClick={templateLocked ? () => setUpgradeOpen(true) : doDownloadDocx}
                disabled={!templateLocked && (downloadingDocx || !editData)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-xs font-mono font-bold transition-colors ${
                  templateLocked
                    ? "border-[#7C3AED]/30 text-[#7C3AED]/70 cursor-pointer hover:bg-[#7C3AED]/5"
                    : "border-[#0025D8] text-[#0025D8] hover:bg-[#0025D8]/5 disabled:opacity-50 disabled:cursor-not-allowed"
                }`}
              >
                {templateLocked ? <Lock className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                {templateLocked ? "Premium" : downloadingDocx ? "Generating…" : "Download DOCX"}
              </button>
            </div>
          </div>
          <div className="border border-border overflow-auto max-h-[600px] bg-gray-50">
            <div className="transform scale-[0.85] origin-top-left" style={{ width: "117.6%" }}>
              {editData
                ? <PreviewComponent data={editData} />
                : <div className="p-8 text-center text-muted-foreground text-sm">No resume data to preview</div>}
            </div>
          </div>
        </div>
      )}
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason="resume_templates" />
    </div>
  );
}
